import { CSSProperties } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  caption,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  letterSpacing,
  spacing,
  subtleShadow,
  heading3,
} from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { RokuButtonContent } from 'modules/graphql';
import { ANIMATION_DELAY, ANIMATION_DURATION, IN_APP_VIEW_OPTIONS } from '../constants';
import { PreviewContentContentContainer } from '../../common/EditorContentContainer.styled';
import { formatHexForState, formatRokuHexForUI } from '../utils';

export const messageUnset: CSSProperties = {
  ...caption,
  color: colors.textSecondary,
  maxWidth: pxToRem(220),
  textAlign: `center`,
};

const commonDeviceStyle: CSSProperties = {
  alignItems: `stretch`,
  backgroundColor: colors.white,
  border: `${spacing.eighth} solid ${colors.grey25}`,
  borderRadius: pxToRem(20),
  display: `flex`,
  flexDirection: `column`,
  letterSpacing: letterSpacing.pos10,
  margin: `auto`,
  overflow: `hidden`,
  width: `100%`,
  ...subtleShadow,
};

export const rokuDeviceStyle: CSSProperties = {
  ...commonDeviceStyle,
  height: pxToRem(515),
  width: pxToRem(704),
  margin: `0px ${spacing.base}`,
  alignItems: 'end',
  justifyContent: 'flex-end',
};

export const inboxTitle: CSSProperties = {
  alignItems: `flex-start`,
  color: colors.grey90,
  display: `flex`,
  fontSize: fontSizes.s14,
  fontWeight: fontWeights.bold,
  lineHeight: fontSizes.s16,
  flex: `1 1 auto`,
};

export const positionerBase: CSSProperties = {
  maxWidth: `100%`,
  position: `absolute`,
  minWidth: `100%`,
};

export const mobileDeviceStyle: CSSProperties = {
  ...commonDeviceStyle,
  height: pxToRem(600),
  width: pxToRem(320),
};

export const inboxContainerStyle: CSSProperties = {
  ...mobileDeviceStyle,
  alignItems: `center`,
  fontFamily: fonts.sfPro,
  padding: `${spacing.plusHalf} ${spacing.base} 0 ${spacing.base}`,
};

export const desktopDeviceStyle: CSSProperties = {
  ...commonDeviceStyle,
  height: pxToRem(341),
  width: pxToRem(545),
};

export const inboxSubtitle: CSSProperties = {
  alignItems: `flex-start`,
  color: colors.grey90,
  display: `flex`,
  fontSize: fontSizes.s11,
  fontWeight: fontWeights.regular,
  lineHeight: fontSizes.s16,
  opacity: `50%`,
};

export const inboxHeader: CSSProperties = {
  display: `flex`,
  fontSize: fontSizes.s16,
  fontWeight: fontWeights.bold,
  lineHeight: fontSizes.s24,
  columnGap: `37%`,
  marginBottom: spacing.base,
  width: `100%`,
};

export const inboxLine: CSSProperties = {
  alignItems: `flex-start`,
  borderBottom: `${spacing.single} solid ${colors.grey15}`,
  display: `flex`,
  padding: `${spacing.base} 0`,
  justifyContent: `flex-start`,
  width: `100%`,
};

export const sendTimeLabel: CSSProperties = {
  ...inboxSubtitle,
  fontSize: pxToRem(8),
};

export const inboxImage: CSSProperties = {
  width: spacing.x4,
  height: spacing.x4,
  borderRadius: spacing.half,
};

export const inboxText: CSSProperties = {
  display: `flex`,
  flexDirection: `column`,
  height: `100%`,
  justifyContent: `space-between`,
  marginLeft: spacing.half,
};

export const inboxSkeleton: CSSProperties = {
  backgroundColor: colors.grey5,
  borderRadius: spacing.quarter,
};

export const inAppWrapper: CSSProperties = {
  height: `100%`,
  position: `relative`,
};

export const inAppContainerStyle: CSSProperties = {
  ...mobileDeviceStyle,
  padding: `${pxToRem(20)} 0`,
};

export const webContainerStyle: CSSProperties = {
  ...commonDeviceStyle,
  width: pxToRem(545),
  height: pxToRem(341),
  top: `50%`,
  right: `50%`,
  transform: `translate(50%, -50%)`,
  position: `absolute`,
};

export const emptyStateWrapper: CSSProperties = {
  display: `flex`,
  flexDirection: `column`,
  alignItems: `center`,
  justifyContent: `center`,
  height: `100%`,
  width: `100%`,
};

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

export const InAppPreviewContentContainer = styled(PreviewContentContentContainer)<{
  $isWebPreview: boolean;
}>`
  ${({ theme, $isWebPreview }) => `
    ${
      $isWebPreview
        ? `
      padding: 0;
      margin: ${theme.spacing.values.plusHalf};
    `
        : ''
    }
  `}
`;

export const StyledInAppContainer = styled.div<{ $option: IN_APP_VIEW_OPTIONS }>`
  align-items: stretch;
  border: ${({ theme }) => `${theme.spacing.values.eighth} solid ${theme.colors.neutrals.grey25}`};
  border-radius: ${({ theme }) => theme.spacing.utils.pxToRem(20)};
  display: flex;
  flex-direction: column;
  letter-spacing: ${({ theme }) => theme.styles.letterSpacing.pos10};
  margin: auto;
  overflow: hidden;
  width: 100%;
  ${({ $option, theme }) => {
    switch ($option) {
      case IN_APP_VIEW_OPTIONS.inApp:
        return `
          height: ${theme.spacing.utils.pxToRem(600)};
          width: ${theme.spacing.utils.pxToRem(320)};
        `;
      case IN_APP_VIEW_OPTIONS.inbox:
        return `
          align-items: center;
          font-family: ${fonts.sansSerif};
          padding: ${theme.spacing.values.plusHalf} ${theme.spacing.values.base} 0 ${theme.spacing.values.base};
        `;
      case IN_APP_VIEW_OPTIONS.web:
        return `
          width: 100%;
          height: 100%;
          top: 50%;
          right: 50%;
          transform: translate(50%, -50%);
          position: absolute;
        `;
      default:
        return '';
    }
  }}
`;

/** Copied from WebInApp animation */

const fadein = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeout_ = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const slidein_ = keyframes`
  0% { transform: translateX(150%) }
  100% { transform: translateX(0%) }
`;

const slideout = keyframes`
  0% { transform: translateX(0%) }
  100% { transform: translateX(150%) }
`;

const slideuptop = keyframes`
  0% { transform: translateY(-150%) }
  100% { transform: translateY(0%) }
`;

const slidedowntop = keyframes`
  0% { transform: translateY(0%) }
  100% { transform: translateY(-150%) }
`;

const slideupbottom = keyframes`
  0% { transform: translateY(150%) }
  100% { transform: translateY(0%) }
`;

const slidedownbottom = keyframes`
  0% { transform: translateY(0%) }
  100% { transform: translateY(150%) }
`;

export const fadeInStyles = css`
  animation: ${fadein} ${ANIMATION_DURATION}ms;
  opacity: 0;
  animation-delay: ${ANIMATION_DELAY}ms;
`;

export const fadeOutStyles = css`
  animation: ${fadeout_} ${ANIMATION_DURATION}ms;
`;

export const slideInStyles = css`
  animation: ${slidein_} ${ANIMATION_DURATION}ms forwards;
  transform: translateX(150%);
  animation-delay: ${ANIMATION_DELAY}ms;
`;

export const slideOutStyles = css`
  animation: ${slideout} ${ANIMATION_DURATION}ms forwards;
`;

export const slideUpTopStyles = css`
  animation: ${slideuptop} ${ANIMATION_DURATION}ms forwards;
  transform: translateY(-150%);
  animation-delay: ${ANIMATION_DELAY}ms;
`;

export const slideDownTopStyles = css`
  animation: ${slidedowntop} ${ANIMATION_DURATION}ms forwards;
`;

export const slideUpBottomStyles = css`
  animation: ${slideupbottom} ${ANIMATION_DURATION}ms forwards;
  transform: translateY(150%);
  animation-delay: ${ANIMATION_DELAY}ms;
`;

export const slideDownBottomStyles = css`
  animation: ${slidedownbottom} ${ANIMATION_DURATION}ms forwards;
`;

// Roku preview
export const RokuNotificationContainer = styled.div<{
  backgroundColor: string;
  hasButtons: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${spacing.half};
  padding: ${spacing.threeQuarter};
  gap: ${spacing.threeQuarter};

  width: ${pxToRem(250)};
  height: fit-content;

  background-color: ${({ backgroundColor }) => backgroundColor};
`;

export const RokuImage = styled.img<{ isValid: boolean }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  ${({ isValid }) =>
    !isValid &&
    css`
      display: none;
    `}
`;

export const RokuButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RokuButton = styled.div<{ props: Partial<RokuButtonContent> }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${spacing.threeEights} ${spacing.quarter};

  width: ${pxToRem(219)};
  height: ${pxToRem(42)};

  background-color: ${({ props }) =>
    formatHexForState(formatRokuHexForUI(props.buttonColorUnFocused))};
  color: ${({ props }) => formatHexForState(formatRokuHexForUI(props.buttonTextColorUnFocused))};
  border: ${({ props }) =>
    `${spacing.eighth} solid ${formatHexForState(formatRokuHexForUI(props.buttonBorderColor))}`};

  :hover {
    background-color: ${({ props }) =>
      formatHexForState(formatRokuHexForUI(props.buttonColorFocused))};
    color: ${({ props }) => formatHexForState(formatRokuHexForUI(props.buttonTextColorFocused))};
  }
`;

export const RokuHeader = styled.div<{ color: string }>`
  ${heading3}
  width: 100%;
  margin: ${spacing.threeEights} ${spacing.threeEights} 0;
  color: ${({ color }) => color};
  text-align: left;
`;

export const RokuBody = styled.div<{ color: string }>`
  ${caption}
  width: 100%;
  margin: ${spacing.threeEights} ${spacing.threeEights} 0;
  color: ${({ color }) => color};
  text-align: left;
`;
